import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

/**
 * 文案表。
 *
 * ⚠ 错误文案按 **`code`** 组织，而不是按界面位置 —— 契约 §2 说得明白：
 *   响应里的 `message` 是**面向开发者**的，不要直接展示给用户。
 *   所以每个出网错误码都要在这里有一条自己的中文，表外的码落 `error.unknown`。
 *
 * ⚠ 键里带点(`error.AUTH.CODE_INVALID`)是有意的：i18next 默认用 `.` 作层级分隔，
 *   于是 `messageKeyOf('AUTH.CODE_INVALID')` 生成的键天然就能查到下面的嵌套结构。
 */

const zhCN = {
  planner: {
    label: '旅行攻略',
    duration: '{{count}} 天',
    startDate: '{{date}} 出发',
    itinerary: '每日行程',
    day: '第 {{day}} 天',
    reason: '推荐理由 · ',
    dailyTips: '当日提醒',
    tips: '出行提醒',
    unavailable: '这份旅行攻略暂时无法展示，请稍后重试。',
  },
  appName: 'Cione',
  common: {
    loading: '正在准备…',
    offline: '连不上服务器，请检查网络后重试',
    retry: '重试',
    cancel: '取消',
    confirm: '确定',
    save: '保存',
    /** 只用来关掉一条提示。⚠ 与「取消」分开：那个是撤销一个动作，这个只是把话读完了。 */
    dismiss: '知道了',
    traceHint: '排查编号 {{traceId}}',
  },
  home: {
    // ⚠ 口径：登录前置(2026-09-02 方案 A)。不要再写「现在就能聊」那类先用后登录的话
    greetingAnon: '你好，说说你想去哪儿 —— 点发送时会先请你登录',
    greetingUser: '你好，{{name}}',
    levelAnon: '未登录',
    levelUser: '已登录',
    levelAdmin: '管理员',
    startChat: '开始对话',
    chatPlaceholder: '对话页在 M2 —— 现在这里只验证认证链路',
    myPage: '我的',
    demoRestricted: '试一个需要登录的动作',
    demoRestrictedLabel: '查看我的资料',
    demoDone: '已完成：{{label}}',
  },
  /**
   * 左侧模块栏。
   *
   * ⚠⚠ `isolationHint` 不是客套话，是**能力边界**：两个模块后面是两套完全独立的服务，
   *   会话、记忆、历史都不互通。不说的话，用户在通用助手里接着问「帮我订刚才那班机票」
   *   是必然会发生的事 —— 而那边既订不了票、也读不到这边的上下文，且**不会报错**。
   */
  nav: {
    modules: '模块',
    openModules: '打开模块栏',
    travel: '旅行规划',
    travelDesc: '机票 · 酒店 · 打车',
    plans: '我的规划',
    plansDesc: '已生成的旅行方案',
    assistant: '通用助手',
    assistantDesc: '问答 · 联网 · 配图',
    isolationHint: '两个模块各自独立：通用助手看不到旅行规划这边的行程与订单，也不能订票。',
  },
  /**
   * 通用助手（后端 universal_mode）。
   *
   * ⚠ 这个服务**没有错误码表** —— 它的报错就是一句中文，直接显示。所以这里只放
   *   「我们自己要说的话」，不要照契约那边再造一份按 code 查表的结构。
   */
  assistant: {
    empty: '问点什么都行 —— 写作、代码、翻译、查资料',
    emptyAnon: '登录后开始对话 —— 会话按账号存在服务端',
    // ⚠⚠ 空屏上必须说清能力边界，用户第一句最可能就是「帮我订张机票」
    scopeHint: '这里是通用问答，不订票、不查订单，也看不到「旅行规划」那边的对话。',
    placeholder: '说点什么…',
    inputLabel: '输入框',
    send: '发送',
    // ⚠⚠ 不能只写「停止」：后端没有取消端点，这一轮在服务端会照跑到底并存进历史
    stop: '停止接收',
    thinking: '正在思考…',
    noContent: '（这一轮没有内容）',
    newSession: '新对话',
    sessions: '历史对话',
    sessionsEmpty: '还没有对话记录',
    sessionUntitled: '未命名对话',
    sessionMeta: '{{n}} 条 · {{time}}',
    sessionListFailed: '会话列表读取失败',
    sessionLoadFailed: '这条会话读不出来',
    reasoning: '思考过程',
    sources: '{{n}} 条来源',
    sourceNoTitle: '（无标题）',
    imagesPending: '正在找配图…',
    interrupted: '连接断了，这条回答没说完',
    // ⚠ 先给「取回」再给「重发」：这个服务的答案是落库的，取回比重跑一轮便宜得多
    recover: '取回这一轮',
    recoverEmpty: '服务端没有这一轮的回答，只能重新发送',
    recoverFailed: '取不回来，请重新发送',
    resend: '重新发送',
    sendFailed: '发送失败，请稍后再试',
    docUploadFailed: '文档上传失败',
    queued: '前面还有 {{position}} 个请求在排队…',
    metaLine: '{{model}} · {{seconds}} 秒',
    model: '模型',
    thinkingToggle: '深度思考',
    searchToggle: '联网搜索',
    // ⚠ 说清这是「强制开」，不是总闸 —— 关着它后端照样会自己判断要不要联网
    searchToggleHint: '默认由 AI 自己判断要不要联网；打开是强制每轮都搜。',
    attachImage: '图片',
    attachImageHint: '最多 {{max}} 张，每张 10MB',
    attachVideo: '视频',
    attachDoc: '文档',
    attachDocHint: '最多 {{max}} 个，支持 PDF / Word / Excel / PPT / txt 等',
    uploading: '上传中…',
    removeAttachment: '移除 {{name}}',
    attachedVideo: '含视频',
    attachedDocs: '含 {{n}} 个文档',
    tooManyImages: '图片最多 10 张，多出来的没有添加',
    imageTooLarge: '有图片超过 10MB，已跳过',
    videoTooLarge: '视频不能超过 100MB',
    tooManyDocs: '文档最多 2 个',
    attachmentRejected: '这个附件没能添加',
    /*
      语音输入。
      ⚠⚠ 麦克风那四条**刻意分开写**：权限被拒要去浏览器设置里放开、没有设备要插一个、
        非安全上下文是这个环境根本给不了、其它是真出错了 —— 用户要做的事完全不同。
        合成一句「录音失败」的下场是他反复点那颗按钮。
      ⚠ `voiceEmpty`（识别出空串）**不是失败**：后端那句 `"".join(...).strip()` 本来就可能
        是空的。说成失败会让用户去重试网络，而他该做的是靠近麦克风再说一遍。
    */
    voiceStart: '语音',
    voiceHint: '点一下开始录音，再点一下结束并识别',
    voiceStop: '结束并识别 {{time}}',
    voiceCancel: '取消录音',
    voiceRecording: '正在录音，最长 {{max}} 秒',
    voiceTranscribing: '识别中…',
    voiceTooShort: '这段太短了，再说一次',
    voiceEmpty: '没听清，再说一次',
    transcribeFailed: '语音识别失败，请再试一次',
    micDenied: '麦克风权限被拒绝 —— 在浏览器地址栏的权限设置里放开它再试',
    micMissing: '没有找到可用的麦克风',
    micUnsupported: '这个环境不支持录音（需要 HTTPS 或 localhost）',
    micFailed: '打不开麦克风，请检查是否被其它程序占用',
  },
  chat: {
    empty: '想去哪儿？直接说就行',
    // 未登录时的空状态。⚠ 要说清「先写、点发送时才登录」，而不是「打开就要登录」
    emptyAnon: '登录后开始对话 —— 先写下你想去哪儿，点发送时会请你登录',
    placeholder: '说点什么…',
    inputLabel: '输入框',
    send: '发送',
    stop: '停止',
    stopping: '停止中',
    thinking: '正在思考…',
    noContent: '（已停止，这一轮没有内容）',
    newConversation: '新对话',
    loginRequired: '这一步需要登录',
    continueLabel: '继续刚才那一步',
    // ⚠ 断流：实测重开同一条 channel 拿不回中断期间的内容，所以只能重发一轮
    interrupted: '连接中断了，这条回答没说完',
    /*
      断流之后正在把这一轮从服务端取回来（`lib/turn-recovery.ts`）。
      ⚠ 措辞不说「正在重连」—— 我们没有在重连，流已经没了；做的是**去库里取**那一轮。
        说成重连的话，取不回来时用户会以为是网络还没好，而其实该点重新发送了。
    */
    recovering: '连接断了，正在把这一轮取回来…',
    resend: '重新发送',
    /*
      ⚠⚠ 历史回放里**解不开**的卡片（W4）。与 `unknownCardDev`（不认识的 type）分开：
        那个是正常降级、只在开发期显示；这个是数据坏了，而历史里**没有文本可退**
        （后端有卡片时话术只剩一句概述），所以必须明说，不能静默跳过。
      ⚠ 文案不写「加载失败，请重试」—— 重试也不会变好，那是永久残缺的一屏。
    */
    brokenCard: '这张卡片无法展示',
    /*
      ⚠⚠ 从历史接回来时的提示。两件事都得说，而且都不是客套 ——
        ① AI 只拿得到最近 10 轮（ai_core `_run_turn` 的 `max_rounds=10`），
           而用户在回看页翻得到全部；
        ② 粘性断点：上次若停在某个选择上，这一句会被当成对它的回答
           （ai_core `_paused()` 直入断点，不经 supervisor）。
      ⚠ 别写成「已恢复对话」这类只报喜的话 —— 那正好把两条限制都藏起来了。
    */
    resumedNotice:
      '接着这条历史对话聊。AI 只记得最近几轮；如果上次停在某个选择上，它可能接着那一步问。',
    /*
      ⚠⚠ 流上撞到 `AUTH.TOKEN_EXPIRED` 时**专用**的一句（W1-5）。
        不能复用 `error.AUTH.TOKEN_EXPIRED`（「登录已过期，请重新登录」）——
        那句在**请求层**是准的（那条路径上刷新已经试过并失败了），
        在**流**上却是吓人的：access 只有 5 分钟，聊一会儿过期是常态，
        下一个请求会静默续上，用户根本不需要去重新登录。
        照搬那句等于让用户白登一次录，还以为自己被登出了。
      ⚠ 说「再试一次」就必须真有得点 —— 所以气泡上同时给了「重新发送」。
    */
    tokenExpiredRetry: '刚才那句没发出去，再试一次',
    unknownCardDev: '未注册的卡片 type：{{type}}（已降级为文本）',
  },
  /* 结果卡片（W2）。⚠ 卡片上除「点选」外没有任何写操作入口 —— 见 W2 红线。 */
  cards: {
    choose: '选这个',
    flightTitle: '找到 {{count}} 个航班',
    // ⚠ 供应商返回的比展示的多时用这句（option_count > 展示数）；相等时仍用 flightTitle
    flightShowingTop: '共 {{total}} 个方案，展示前 {{count}}',
    hotelTitle: '找到 {{count}} 家酒店',
    nonstop: '直飞',
    stops: '经停 {{count}} 次',
    // ⚠ 只在 1~8 之间显示：9 = 9 个及以上，0 = **未知**（照字面写「剩 0 张」会把还能买的说成卖光了）
    seatsLeft: '剩 {{count}} 张',
    perNight: '{{price}}/晚',
    score: '{{score}} 分',
    hotelUnnamed: '（未提供名称）',
    /*
      ⚠ 这一条是**发出去的那句话**，会作为用户气泡出现在对话里，所以它要跟界面语言走。
      ⚠ 后端酒店选择靠 **LLM 抽槽**（`hotel/agent.py` 抽出 `hotel_index`），
        不是正则，所以说法不必死板 —— 但**必须带上序号**：
        `_pick_hotel` 只认 `hotel_index`（名字只是兜底的模糊匹配）。
      ⚠ 机票那张卡刚好相反：`_match_choice` 是**真正则**，所以那边发的是航班号本身，
        不走 i18n（见 `flight-options.tsx`）。
      ⚠ 2026-09-03 曾因后端正则吃不下含数字的航司码，在这里加过一条
        `chooseFlightByIndexUtterance`（「第 N 个」）临时降级；后端修好后已删。
        **别再加回来** —— 理由记在 `flight-options.tsx` 头注里。
    */
    chooseHotelUtterance: '第 {{idx}} 家',
    /*
      `flight_traveler_required`（机票域等待点 ⑤）。
      ⚠ `message` 后端一般会给，这两条只是兜底 —— 缺字段不许白屏。
      ⚠ 人数缺失时用不带人数那条：**说错人数比不说更糟**。
    */
    travelerRequired: '还没有乘机人，先去添加一位再继续',
    travelerRequiredNeed: '本次需要 {{count}} 位乘机人，先去添加再继续',
    addTraveler: '去添加乘机人',
    travelerAdded: '加好了，继续',
    /*
      ⚠ 这是**发出去的那句话**，会作为用户气泡上屏，所以跟界面语言走。
      ⚠ 内容不重要、但**不能踩后端的放弃词表**：`_ABORT_RE` 认「算了|取消|不买|不要了|
        不订|先不|不用了|不确认|不了$」—— 命中就直接收尾、整轮作废。「加好了」是安全的。
      ⚠ 后端拿到任何一句话都会**重查花名册**再决定（`_await_roster` 的注释：
        判据只能是花名册本身），所以这句话措辞不参与判定。
    */
    travelerAddedUtterance: '加好了',
    /*
      `flight_confirmation`（金额确认闸门，2026-09-04）。
      ⚠ 这张卡只做「摘要 + 按钮」：航班/行李/退改的全文在上面那个气泡里，这里**不重排**。
    */
    quoteValidUntil: '报价有效期至 {{at}}，过期可能需要重新验价',
    confirmBuy: '确认购买',
    /* ⚠ 金额写进按钮：用户按下去时看着的就是要付的那个数 */
    confirmBuyWithPrice: '确认购买 {{price}}',
    /* 两步确认展开后那颗键。⚠ 与 `common.confirm`（「确定」）分开写：这一颗是要花钱的 */
    confirmBuyFinal: '确定，下单',
    changeFlight: '换一个',
    notNow: '暂不购买',
    /*
      ⚠⚠ 下面三句是**发出去的那句话**，会作为用户气泡上屏。
      ⚠⚠ 它们同时要被后端 `classify_pause_reply` 认出来，**措辞不能随手改**：
        `确认` 命中 `_CONFIRM_RE`；`换一个` 命中 `_CHANGE_RE`（且不含「换成/改成」，
        不会被判成"改搜索条件"而回去重搜）；`算了` 命中 `_ABORT_RE`。
        改成「再看看」这类会落进 `vague` —— 连着两次含糊后**整轮作废**。
      ⚠ 英文界面下这三句**仍然发中文**（见 en 侧同名键）：后端只认中文词表，
        翻过去等于按钮点了没用。这不是漏翻，是故意的。
    */
    confirmUtterance: '确认',
    changeUtterance: '换一个',
    abortUtterance: '算了',
    /*
      `ride_confirm`（打车确认卡，2026-09-11 接）。
      🔴 价格这三条分两档，**不能合成一条**：`estimated_price_yuan` 是**字符串**，
        出租车打表时它就是「打表计价」四个字。写成「预估 ¥0.00」会把打表说成免费，
        而这是下单前最后一眼。
      ⚠ 确认那句话**不在这里**：它是 contracts 的 `RIDE_CONFIRM_UTTERANCE`。
        后端 `_CONFIRM_EXACT` 是**整句精确匹配**，措辞是契约的一部分、不是文案 ——
        放进 i18n 迟早有人把它翻成英文或改成「确认叫车」，那就是点了没反应且不报错。
        （机票那三句 utterance 放在这里是历史写法，靠上面那段注释挡着；新写的别学。）
    */
    rideEstimatedPrice: '预估 ¥{{price}}',
    rideMeteredPrice: '打表计价（上车打表，下车结算）',
    rideConfirmCall: '确认叫车',
    rideConfirmCallFinal: '确定，叫车',
    /* ⚠ 换车走**序号**，不走按钮 —— 后端卡片文字自己也是这么写的 */
    rideSwitchHint: '想换一辆，直接回复序号',
    /*
      `ride_token_required`（打车账号绑定闸门，M2.5，2026-09-18 接）。
      🔴 下面前三条是**同一件事的三种处境**，合并任意两条都会说错话：
        `UNBOUND` 他从没绑过 —— 说「失效了」会让他去找一个并不存在的旧绑定；
        `INVALID` 他绑过但用不了了 —— 说「请先绑定」会让他以为上次根本没绑成；
        认不出的 reason（含缺失）走中性那条 —— **不许**默认成 `UNBOUND` 那句。
      ⚠ `rideTokenInvalid` **不能只说「过期了」**：后端是"懒发现"（录入只校验格式，
        真伪要到第一次实际叫车时才由供应商判定），用户可能**刚绑完三秒**就看到它。
      ⚠ 后端一般会在卡片的 `message` 里给好话术，这三条只是兜底 —— 缺字段不许白屏。
      ⚠ 自动发出去的那句「帮我打车」**不在这里**：它是 contracts 的
        `RIDE_TOKEN_BOUND_UTTERANCE`，后端的词表只认中文，放进文案表迟早被翻成英文。
    */
    rideTokenUnbound: '先绑一下你自己的出行账号，绑好才能帮你叫车',
    rideTokenInvalid: '刚才那个用不了 —— 可能过期了，也可能复制时少了一截，麻烦重新取一次',
    rideTokenNeutral: '叫车要用你自己的出行账号，按下面的步骤取一次 Token 贴进来',
    /* ⚠ 仅 `reason=INVALID` 时才有 `masked`。可以显示，但不要读出来 / 不进语音播报。 */
    rideTokenPrevious: '你之前绑的是 {{masked}}',
    rideTokenQrAlt: '出行服务二维码',
    rideTokenLabel: '出行 Token',
    rideTokenPlaceholder: '把复制的整段粘到这里',
    rideTokenSubmit: '绑定',
    rideTokenSubmitting: '正在绑定…',
    /*
      ★ 成功。⚠ 两句**不能**写：
        ①「请重新描述你的需求」—— 他绑定之前说的「去机场」还在后端状态里，不用重说；
        ②「已验证 / 可以用了」—— 后端此刻只校验了格式，真伪要到第一次叫车才知道。
    */
    rideTokenBound: '绑定成功，正在接着帮你叫车',
    /*
      `ride_order`（行程实况卡，S8，2026-09-20 接）。
      🔴 这一组里有四条是「**不知道**」与「**是 0**」的分界，合并任意一条都会说错话：
        `rideEta` / `rideEdaMeters` 只在后端**真给了**那个键时才渲染（缺键 = 没有值，
        不是 0 —— 显示「还有 0 分钟 / 0 米」就是告诉用户车已经到了）；
        `rideLocationAge` 同理（判 0 会算出「56 年前」）；
        `rideCancelFeeUnknown` 是**问不到取消费**那一档，**绝不能**并进「免费」那句。
      🔴 `rideCallSupport` 只许说「客服」：打车平台**不提供司机手机号**（官方文档把
        「联系司机」列为不支持），写成「联系司机」就是承诺一件做不到的事。
      ⚠ `rideEstimatedPrice` 与上面 `ride_confirm` 那条**共用**：两处说的是同一个
        「预估价」，措辞分家只会让同一单在两张卡上看起来是两个数。
    */
    rideLiveLoading: '正在获取行程…',
    rideLiveUnknownStatus: '行程进行中',
    rideMapAlt: '行程示意图：起点、终点与车的相对位置',
    rideOngoingTitle: '进行中的行程',
    /* ⚠ 只在 `etaMinutes` 这个键**存在**时显示。缺键 ≠ 0 分钟。 */
    rideEta: '还有 {{count}} 分钟',
    /* ⚠ 同上，只在 `edaMeters` 存在时显示。 */
    rideEdaMeters: '距你 {{value}} 米',
    rideEdaKm: '距你 {{value}} 公里',
    /* 🔴 基准是响应里的 `serverTimeMs`，不是本机时间 —— 本机时钟偏两分钟页面就开始胡说。 */
    rideLocationAge: '{{count}} 秒前更新',
    /*
      ⚠⚠ 只给「暂时够不着打车平台 / 后端开关没开」这两种 —— 它们**沿用上一次的位置**。
        `NOT_IN_TRIP`（还在派单或已结束）什么都不说：那是最常见的情形，
        给它配一句提示等于把常态说成异常。
    */
    rideLocationStale: '位置更新中，显示的是上一次的位置',
    /*
      ⚠⚠ 派单期间司机那一栏是空的（后端 `payload` 里没有 `driver` 键）——
        不说这一句，卡片看起来和坏掉了一模一样。见 `cards/ride-order.tsx`。
      ⚠ 把「会显示什么」写出来（车牌 + 预计到达时间）：用户此刻就是在等这两样，
        只说「正在派单」他还得猜自己该等什么。
    */
    rideAwaitingDriver: '正在为你派单，司机接单后这里会显示车牌和预计到达时间',
    /*
      🔴 账号没绑/失效。⚠ 必须说清**行程本身不受影响** —— 车还是会来，
        不说的话用户会以为要重新叫一次。
    */
    rideCredentialGone:
      '看不到车的位置了：你的出行账号没绑定或已失效。行程本身不受影响，司机照常会来；要确认可以打下面的客服电话。',
    rideCancelTrip: '取消行程',
    rideCancelQuoting: '正在查取消费用…',
    /* ★ 第二步的三档文案。⚠ 三档不能合并，理由见本组头注。 */
    rideCancelFree: '现在取消不收费。确认取消这趟行程吗？',
    rideCancelWithFee:
      '现在取消要收 ¥{{fee}}（由打车平台直接从你的账户扣，我方不经手也退不了）。确认取消吗？',
    rideCancelFeeUnknown: '暂时查不到取消费用，现在取消可能要收费。确认取消吗？',
    rideFreeCancelUntil: '{{at}} 前取消免费',
    rideCancelFinal: '确定，取消行程',
    rideCancelling: '正在取消…',
    rideKeepTrip: '不取消',
    /* ⚠ 只说「已提交」：真实状态以下一次 `/live` 为准（它会回 terminal）。 */
    rideCancelSubmitted: '取消请求已提交，状态稍后更新',
    /* 🔴 客服，不是司机。见本组头注。 */
    rideCallSupport: '联系客服 {{tel}}',
    /* 🔴 给用户看的是**供应商**单号 —— 打客服电话要报的是它，不是我方单号。 */
    rideSupplierOrderNo: '客服单号 {{no}}',
    /* ⚠ 轮询自己断了时的小提示：上面那一屏仍然是好的，只是可能有点旧。 */
    rideLiveStalled: '暂时收不到行程更新',
  },
  login: {
    title: '登录 Cione',
    subtitle: '登录后回到刚才的位置，写好的内容不会丢',
    // 节点级卡片(auth_login_required)那条路径登录后确实会自动继续，所以这句仍然对
    pendingHint: '登录后将继续：{{label}}',
    // ⚠ 发送拦截那条路径用这句：登录后**不自动发送**，用户自己再点一次(产品第 3 点)
    draftKeptHint: '登录后回到你刚才写的内容，你再点一次发送就发出去',
    phone: '手机号',
    phonePlaceholder: '请输入手机号',
    smsCode: '验证码',
    smsCodePlaceholder: '6 位数字',
    password: '密码',
    passwordPlaceholder: '至少 8 位',
    sendCode: '获取验证码',
    resendIn: '{{seconds}} 秒后可重发',
    bySms: '短信验证码登录',
    byPassword: '密码登录',
    submit: '登录',
    close: '以后再说',
    codeSent: '验证码已发送',
  },
  me: {
    title: '我的',
    nickname: '昵称',
    nicknamePlaceholder: '还没有设置',
    phone: '手机号',
    userId: '用户 id',
    level: '身份',
    setPassword: '设置 / 修改密码',
    setPasswordHint: '需要短信验证码二次确认',
    confirmPhone: '手机号',
    confirmPhonePlaceholder: '请填完整手机号（{{masked}}）',
    newPassword: '新密码',
    logout: '退出登录',
    saved: '已保存',
    passwordSaved: '密码已更新',
    anonymousBlocked: '登录后才能管理资料',
    travelers: '出行人',
    orders: '我的订单',
  },
  travelers: {
    title: '出行人',
    subtitle: '常用乘机人，下单时可直接选择',
    empty: '还没有出行人，先添加一位',
    add: '添加出行人',
    edit: '编辑',
    // ⚠ 删除是**归档**（已有订单的退改仍用得到），别写「永久删除」
    remove: '移出花名册',
    removeConfirm: '把「{{name}}」移出花名册？已有订单的退改仍可使用，不是永久删除。',
    searchPlaceholder: '按姓名搜索',
    shared: '企业共享',
    name: '姓名',
    docType: '证件类型',
    docNumber: '证件号',
    docNumberMasked: '证件号',
    // ⚠ 编辑页证件号只读；要改是显式「更换证件」，且类型与号必须同时给
    changeDoc: '更换证件',
    changeDocHint: '更换证件类型必须同时填写新的证件号',
    phone: '手机号',
    // ⚠ 编辑时「留空 = 保持不变」，与常规「整包提交」相反
    keepUnchangedHint: '留空的字段保持不变，不会被清空',
    keepPhonePlaceholder: '当前 {{masked}}，留空保持不变',
    surnamePinyin: '姓（拼音）',
    givenNamePinyin: '名（拼音）',
    birthday: '出生日期',
    gender: '性别',
    nationality: '国籍',
    nationalityCode: '国籍二位码',
    countryOther: '其他…',
    // ⚠ 键是 ISO 3166-1 alpha-2，与 travelers-panel 的 COMMON_NATIONALITIES 一一对应
    country: {
      CN: '中国',
      HK: '中国香港',
      MO: '中国澳门',
      TW: '中国台湾',
      US: '美国',
      GB: '英国',
      JP: '日本',
      KR: '韩国',
      SG: '新加坡',
      AU: '澳大利亚',
      CA: '加拿大',
    },
    docValidTo: '证件有效期至',
    save: '保存',
    saved: '已保存',
    created: '已添加',
    removed: '已移出花名册',
    // ★ 明文纪律：只用于出票、不展示、不缓存
    plaintextHint: '证件号仅用于出票，不会被展示或缓存',
    genderMale: '男',
    genderFemale: '女',
    required: '姓名、证件类型与证件号为必填',
  },
  history: {
    title: '历史对话',
    empty: '还没有历史对话',
    // ⚠ 会话标题就是用户说的第一句话（后端按 seq=1 取），可能什么都没有
    untitled: '（无标题）',
    messageCount: '{{count}} 条',
    open: '查看',
    remove: '删除对话',
    // ⚠ 软删，但整段对话都不再出现 —— 别写「归档」，那是出行人那边的语义
    removeConfirm: '删除',
    removeDone: '已删除',
    // ⚠⚠ 删的是当前正聊着的那条：界面已就地清空，得说一声，否则用户以为消息自己没了
    removedCurrent: '已删除，当前对话已清空',
    replayTitle: '回看',
    // ⚠ 出口就挨着「这里是回看」那句给 —— 只说做不了、不说去哪做，等于把人扔在半路
    continueHere: '在这条对话里继续',
    // ⚠ 空数组 = 到头了，不是出错
    earlier: '加载更早',
    noEarlier: '没有更早的了',
    notFound: '找不到这条对话',
    back: '返回列表',
    // ⚠ 只读快照：模型侧只记得最近若干轮，给「接着聊」会让人以为 AI 全都记得
    readOnly: '这里是回看，发消息请回到对话页',
  },
  orders: {
    title: '我的订单',
    empty: '还没有订单',
    all: '全部',
    detail: '订单详情',
    orderNo: '订单号',
    createdAt: '下单时间',
    amount: '金额',
    supplierOrderNo: '供应商订单号',
    // ⚠ 未支付时供应商订单号为空是正常的
    notIssuedYet: '出票后生成',
    cancel: '取消订单',
    // ⚠ 取消 ≠ 退票：仅出票前撤单
    cancelHint: '仅出票前撤单；已出票需退票请联系客服',
    cancelConfirm: '确定取消这个订单吗？',
    cancelReason: '取消原因（可选）',
    cancelDone: '已提交，当前状态：{{state}}',
    notFound: '找不到这个订单',
    payloadTitle: '行程信息',
    // ⚠ domainPayload parse 失败要降级，不白屏
    payloadBroken: '行程信息暂时无法显示',
    back: '返回列表',
    /* ⚠ 键是**大写**：后端存的是 proto 枚举名去 `DOMAIN_` 前缀（见 contracts/orders.ts） */
    domain: {
      FLIGHT: '机票',
      HOTEL: '酒店',
      RIDE: '打车',
    },
    /*
      🔴 打车单的金额是**预估价**，不是应收 —— 打车域供应商直收（腾讯出行行程结束后
      自己向用户收），我方一分不经手。不标「预估」，用户会以为这是要付给我们的钱。
    */
    estimatedAmount: '预估 {{price}}',
    /*
      ⚠⚠ 已付款取消后落 `ORDER_REFUNDING`。**不能说「已退款」** ——
      钱是真会退的（后端 09-11 退款原路退回闭环，v1 全额退），但不是同步发生的：
      取消只把订单推到退款中，真正发出退款的是补偿任务（分钟级）。
    */
    refundStarted: '已受理，退款处理中 —— 款项将原路退回，到账时间以渠道为准',
    /* ⚠ 取消 ≠ 退票：已付款那档要单独说清钱怎么退 */
    cancelPaidHint:
      '这笔订单已付款，取消后将全额原路退款；⚠ 已出票需退票请联系客服（退票与取消不是一件事）',
    pay: {
      title: '支付',
      action: '去支付',
      actionWithPrice: '去支付 {{price}}',
      /*
        ⚠⚠ 这句话要经得起追问，所以说的是**实情**：后端今天只开了微信 **APP 支付**，
          那七个参数要原生 SDK 才拉得起来；支付宝尚未接入。⇒ 浏览器里没有可用形态。
        ⚠ 刻意**不给一个点了没反应的按钮** —— 用户是带着钱来的，死按钮会让人以为
          钱扣了没到账。
      */
      unavailableBody:
        '当前浏览器里暂时无法付款：目前只开通了微信 App 支付，请在 Cione App 内完成。订单已保留，不会因此失效。',
      /* ⚠⚠ CONFLICT 多半是竞态而不是错误，语气必须中性 —— 契约点名要求的 */
      raceRetry: '订单状态刚刚更新，请稍候再试一次',
      expiresIn: '本次支付 {{time}} 后失效',
      /* ⚠ 过期不是错误：重新点一次即可，出口是幂等的 */
      expired: '本次支付已超时，重新点一次即可',
      /* ⚠ 只说「等结果」，不说「已支付」—— 到账与否只有订单状态说了算 */
      waiting: '已发起支付，正在等待结果…支付完成后本页会自动更新',
      cancelled: '已取消支付，订单仍然保留',
      paid: '已收到款项，订单进入出票流程',
      /* ⚠ 七个参数缺一个就不递给 SDK：半份参数的表现是「拉起没反应」且不报错 */
      paramsBroken: '支付参数不完整，已中止拉起，请稍后重试或联系客服',
      noPush: '支付结果以订单状态为准',
    },
    state: {
      ORDER_CREATED: '已创建',
      ORDER_PENDING_PAYMENT: '待支付',
      ORDER_PAID: '已支付',
      ORDER_FULFILLING: '出票中',
      ORDER_COMPLETED: '已完成',
      ORDER_CANCELLED: '已取消',
      ORDER_REFUNDING: '退款中',
      ORDER_REFUNDED: '已退款',
      ORDER_FAILED: '已失败',
    },
  },
  docTypes: {
    IC: '身份证',
    PP: '护照',
    GA: '港澳通行证',
    TB: '台胞证',
    OTHER: '其他证件',
  },
  error: {
    unknown: '出了点问题，请稍后再试',
    AUTH: {
      LOGIN_REQUIRED: '请先登录',
      TOKEN_EXPIRED: '登录已过期，请重新登录',
      TOKEN_INVALID: '登录状态失效，请重新登录',
      CODE_INVALID: '验证码不正确',
      CODE_EXPIRED: '验证码已过期，请重新获取',
      CODE_TOO_FREQUENT: '发送太频繁，请稍后再试',
      PASSWORD_INVALID: '手机号或密码不正确',
      ACCOUNT_DISABLED: '账号已被停用，请联系客服',
      ACTION_FORBIDDEN: '这一步需要登录后才能继续',
    },
    COMMON: {
      INTERNAL: '服务出了点问题，请稍后再试',
      INVALID_ARGUMENT: '填写的内容有误，请检查后重试',
      FAILED_PRECONDITION: '当前状态下无法完成这个操作',
      UNAUTHENTICATED: '请先登录',
      FORBIDDEN: '没有权限进行这个操作',
      NOT_FOUND: '找不到对应的内容',
      CONFLICT: '状态已经变了，请刷新后重试',
      RATE_LIMITED: '当前访问量较大，请稍后再试',
      UPSTREAM_UNAVAILABLE: '上游服务暂时不可用，请稍后再试',
    },
    IDENTITY: {
      USER_NOT_FOUND: '找不到这个用户',
      EMAIL_EXISTS: '这个邮箱已经被使用',
      VERSION_CONFLICT: '资料已被其他设备修改，请刷新后重试',
      DOC_INVALID: '证件信息有误，请核对后重填',
      TRAVELER_NOT_FOUND: '找不到这位出行人',
      TRAVELER_FORBIDDEN: '没有权限查看这位出行人',
      TRAVELER_ARCHIVED: '这位出行人已归档',
      VAULT_NOT_CONFIGURED: '服务端配置缺失，请联系技术支持',
      AUDIT_WRITE_FAILED: '操作未能留痕，已中止',
      /*
        ⚠ 打车 Token 的**格式**不合（空 / 过长 / 含换行等不可见字符）。
        ⚠⚠ 它**不说明 Token 真伪** —— 后端录入时只校验格式（"懒发现"）。
          所以别写成「Token 无效」：用户刚从微信复制的那段，多半只是粘漏了一截。
      */
      CREDENTIAL_FORMAT_INVALID: '格式不对，请重新复制一次完整的 Token',
    },
    TRADE: {
      // ⚠ 文案方向都照契约 §3.4 定，别写绝对：
      //   INVENTORY_GONE 是「认不出的拒绝」的兜底，不能断定一定没座位 → 别写「已售罄」；
      INVENTORY_GONE: '这班订不到了，换一班看看',
      //   PRICE_CHANGED 说不出新价、也不知方向 → 别写「涨价了」；
      PRICE_CHANGED: '价格有变动，请重新确认这一班',
      QUOTE_EXPIRED: '报价已过期，请重新搜索',
      TRAVELER_DOC_INVALID: '证件信息有问题，请到出行人里核对',
      // ⚠ 文案必须明确「不要重发」：供应商侧可能已经成功，重发会重复扣款
      RESULT_UNKNOWN: '结果待确认，请勿重复提交 —— 请到订单里查看最新状态',
      //   UNAVAILABLE 多发生在出票阶段、用户已付款 → 绝不能是「换一班」
      UNAVAILABLE: '暂时无法出票，请联系客服',
    },
    CLIENT: {
      NETWORK: '网络不可用，请检查连接后重试',
      TIMEOUT: '请求超时，请稍后再试',
      ABORTED: '操作已取消',
      CONTRACT_DRIFT: '客户端与服务端版本不一致，请更新后重试',
    },
  },
} as const

const en = {
  planner: {
    label: 'Travel guide',
    duration: '{{count}} days',
    startDate: 'Starts {{date}}',
    itinerary: 'Daily itinerary',
    day: 'Day {{day}}',
    reason: 'Why visit · ',
    dailyTips: 'Daily tips',
    tips: 'Travel tips',
    unavailable: 'This travel guide is unavailable. Please try again later.',
  },
  appName: 'Cione',
  common: {
    loading: 'Getting ready…',
    offline: 'Cannot reach the server. Check your connection and retry.',
    retry: 'Retry',
    cancel: 'Cancel',
    confirm: 'OK',
    save: 'Save',
    dismiss: 'Got it',
    traceHint: 'Trace {{traceId}}',
  },
  home: {
    greetingAnon: 'Hi — tell me where you want to go. Sending asks you to sign in first.',
    greetingUser: 'Hi, {{name}}',
    levelAnon: 'Not signed in',
    levelUser: 'Signed in',
    levelAdmin: 'Administrator',
    startChat: 'Start chatting',
    chatPlaceholder: 'The chat page lands in M2 — this screen only verifies auth.',
    myPage: 'Profile',
    demoRestricted: 'Try an action that needs sign-in',
    demoRestrictedLabel: 'Open my profile',
    demoDone: 'Done: {{label}}',
  },
  nav: {
    modules: 'Modules',
    openModules: 'Open modules',
    travel: 'Trip planning',
    travelDesc: 'Flights · Hotels · Rides',
    plans: 'My plans',
    plansDesc: 'Your generated itineraries',
    assistant: 'General assistant',
    assistantDesc: 'Q&A · Web search · Images',
    isolationHint:
      'The two modules are separate: the general assistant cannot see your trips or orders, and cannot book anything.',
  },
  assistant: {
    empty: 'Ask anything — writing, code, translation, research',
    emptyAnon: 'Sign in to start — conversations are stored per account',
    scopeHint:
      'This is general Q&A. It cannot book or look up orders, and it cannot see your Trip planning conversation.',
    placeholder: 'Say something…',
    inputLabel: 'Message',
    send: 'Send',
    stop: 'Stop receiving',
    thinking: 'Thinking…',
    noContent: '(Nothing came back for this turn)',
    newSession: 'New chat',
    sessions: 'History',
    sessionsEmpty: 'No conversations yet',
    sessionUntitled: 'Untitled',
    sessionMeta: '{{n}} msgs · {{time}}',
    sessionListFailed: 'Could not load the conversation list',
    sessionLoadFailed: 'Could not open that conversation',
    reasoning: 'Reasoning',
    sources: '{{n}} sources',
    sourceNoTitle: '(untitled)',
    imagesPending: 'Looking for images…',
    interrupted: 'The connection dropped before this answer finished',
    recover: 'Fetch this turn',
    recoverEmpty: 'The server has no answer for this turn — please send again',
    recoverFailed: 'Could not fetch it — please send again',
    resend: 'Send again',
    sendFailed: 'Could not send. Please try again.',
    docUploadFailed: 'Document upload failed',
    queued: '{{position}} request(s) ahead of you…',
    metaLine: '{{model}} · {{seconds}}s',
    model: 'Model',
    thinkingToggle: 'Deep thinking',
    searchToggle: 'Web search',
    searchToggleHint:
      'By default the AI decides when to search. Turning this on forces it every turn.',
    attachImage: 'Image',
    attachImageHint: 'Up to {{max}}, 10MB each',
    attachVideo: 'Video',
    attachDoc: 'Document',
    attachDocHint: 'Up to {{max}} — PDF / Word / Excel / PPT / txt and more',
    uploading: 'Uploading…',
    removeAttachment: 'Remove {{name}}',
    attachedVideo: 'with video',
    attachedDocs: 'with {{n}} document(s)',
    tooManyImages: 'Up to 10 images — the extras were not added',
    imageTooLarge: 'Some images are over 10MB and were skipped',
    videoTooLarge: 'Video must be under 100MB',
    tooManyDocs: 'Up to 2 documents',
    attachmentRejected: 'That attachment could not be added',
    voiceStart: 'Voice',
    voiceHint: 'Click to start recording, click again to stop and transcribe',
    voiceStop: 'Stop & transcribe {{time}}',
    voiceCancel: 'Discard',
    voiceRecording: 'Recording — up to {{max}}s',
    voiceTranscribing: 'Transcribing…',
    voiceTooShort: 'That was too short — please say it again',
    voiceEmpty: "Didn't catch that — please say it again",
    transcribeFailed: 'Transcription failed. Please try again.',
    micDenied: 'Microphone access was denied — allow it in your browser settings and retry',
    micMissing: 'No microphone found',
    micUnsupported: 'This environment cannot record (needs HTTPS or localhost)',
    micFailed: 'Could not open the microphone — check whether another app is using it',
  },
  chat: {
    empty: 'Where to? Just say it.',
    emptyAnon: 'Sign in to start. Write where you want to go — sending asks you to sign in.',
    placeholder: 'Say something…',
    inputLabel: 'Message',
    send: 'Send',
    stop: 'Stop',
    stopping: 'Stopping',
    thinking: 'Thinking…',
    noContent: '(Stopped — nothing came back)',
    newConversation: 'New chat',
    loginRequired: 'This step requires signing in',
    continueLabel: 'continue where you left off',
    interrupted: 'The connection dropped; this answer is incomplete.',
    /* Not "reconnecting" — the stream is gone; we're fetching the turn the server already saved. */
    recovering: 'Connection dropped — fetching this answer from the server…',
    resend: 'Send again',
    brokenCard: 'This card cannot be shown',
    resumedNotice:
      'Continuing a past chat. The assistant only remembers the last few turns; if it was waiting on a choice, your next message may be read as the answer.',
    tokenExpiredRetry: "That one didn't go through — give it another try",
    unknownCardDev: 'Unregistered card type: {{type}} (degraded to text)',
  },
  cards: {
    choose: 'Choose',
    flightTitle: '{{count}} flights found',
    flightShowingTop: 'Top {{count}} of {{total}} flights',
    hotelTitle: '{{count}} hotels found',
    nonstop: 'Nonstop',
    stops: '{{count}} stop(s)',
    seatsLeft: '{{count}} left',
    perNight: '{{price}}/night',
    score: '{{score}}/5',
    hotelUnnamed: '(no name)',
    /* ⚠ Must keep the index — the backend slot extractor looks for `hotel_index`. */
    chooseHotelUtterance: 'Hotel #{{idx}}',
    /* `flight_traveler_required` fallbacks — never blank on a missing field. */
    travelerRequired: 'Add a passenger before we can continue',
    travelerRequiredNeed: 'This booking needs {{count}} passenger(s) — add them to continue',
    addTraveler: 'Add a passenger',
    travelerAdded: "Done — let's continue",
    /* ⚠ Sent as the user's own message. Must NOT match the backend abort word list. */
    travelerAddedUtterance: 'Added',
    /* `flight_confirmation` — summary + buttons only; the full text is in the bubble above. */
    quoteValidUntil: 'Price held until {{at}} — after that it may need re-pricing',
    confirmBuy: 'Confirm purchase',
    confirmBuyWithPrice: 'Confirm purchase {{price}}',
    confirmBuyFinal: 'Yes, book it',
    changeFlight: 'Pick another',
    notNow: 'Not now',
    /*
      ⚠⚠ These three stay in Chinese **on purpose**, unlike every other utterance here.
        The backend gate (`classify_pause_reply`) matches Chinese words only —
        translating them makes the buttons do nothing. Not a missing translation.
    */
    confirmUtterance: '确认',
    changeUtterance: '换一个',
    abortUtterance: '算了',
    /*
      `ride_confirm`. ⚠ Two price lines, not one: `estimated_price_yuan` is a STRING and
      is the literal「打表计价」for metered taxis — rendering that as "est. ¥0.00" would
      tell the user the ride is free, on the last screen before booking.
      ⚠ The confirm utterance lives in contracts (`RIDE_CONFIRM_UTTERANCE`), not here:
      the backend matches the whole sentence exactly, so it is contract, not copy.
    */
    rideEstimatedPrice: 'Est. ¥{{price}}',
    rideMeteredPrice: 'Metered (pay the meter at drop-off)',
    rideConfirmCall: 'Confirm ride',
    rideConfirmCallFinal: 'Yes, book it',
    rideSwitchHint: 'Want a different car? Just reply with its number.',
    /*
      `ride_token_required` (M2.5). 🔴 Three separate lines on purpose — merging any two
      of them tells the user something false:
        UNBOUND = never bound; INVALID = bound but unusable; unknown reason = neutral.
      ⚠ The INVALID line must NOT say only "expired": the backend validates format only
        at bind time, so the user may see it three seconds after binding.
      ⚠ The auto-sent utterance is NOT here — it lives in contracts
        (`RIDE_TOKEN_BOUND_UTTERANCE`) and stays Chinese; the backend matches Chinese only.
    */
    rideTokenUnbound: 'Link your own ride account first — we need it to book the car',
    rideTokenInvalid:
      "That one didn't work — it may have expired, or part of it may have been cut off when copying. Please grab it again.",
    rideTokenNeutral:
      'Booking a car uses your own ride account — follow the steps below and paste the token in',
    rideTokenPrevious: 'Previously linked: {{masked}}',
    rideTokenQrAlt: 'Ride service QR code',
    rideTokenLabel: 'Ride token',
    rideTokenPlaceholder: 'Paste the whole thing here',
    rideTokenSubmit: 'Link account',
    rideTokenSubmitting: 'Linking…',
    /* ⚠ Do not say "verified" (format only) and do not ask the user to restate the trip. */
    rideTokenBound: 'Linked — carrying on with your ride',
    /*
      `ride_order` (S8). 🔴 Four of these draw the line between "no value" and "zero":
        `rideEta` / `rideEdaMeters` / `rideLocationAge` render ONLY when the backend
        actually sent that key (a missing key is not 0 — "0 min away" tells the user
        the car has arrived, and `serverTimeMs - 0` reads as "56 years ago");
        `rideCancelFeeUnknown` is the "we could not get the fee" case and must NEVER
        be merged into the free-cancellation line.
      🔴 `rideCallSupport` says SUPPORT, never "driver": the ride platform does not
        expose driver phone numbers at all.
    */
    rideLiveLoading: 'Fetching your ride…',
    rideLiveUnknownStatus: 'Ride in progress',
    rideMapAlt: 'Schematic: pickup, drop-off and the car',
    rideOngoingTitle: 'Rides in progress',
    rideEta: '{{count}} min away',
    rideEdaMeters: '{{value}} m away',
    rideEdaKm: '{{value}} km away',
    rideLocationAge: 'updated {{count}}s ago',
    rideLocationStale: 'Updating location — showing the last known position',
    rideAwaitingDriver:
      'Finding you a driver — the plate and ETA show up here once someone accepts',
    rideCredentialGone:
      "We can't show the car's location: your ride account is not linked or no longer works. The ride itself is unaffected — the driver is still coming. Call support below to confirm.",
    rideCancelTrip: 'Cancel ride',
    rideCancelQuoting: 'Checking the cancellation fee…',
    rideCancelFree: 'Cancelling now is free. Cancel this ride?',
    rideCancelWithFee:
      'Cancelling now costs ¥{{fee}}, charged to you directly by the ride platform — we neither collect nor refund it. Cancel anyway?',
    rideCancelFeeUnknown:
      "We couldn't fetch the cancellation fee, so cancelling now may cost you. Cancel anyway?",
    rideFreeCancelUntil: 'Free to cancel until {{at}}',
    rideCancelFinal: 'Yes, cancel the ride',
    rideCancelling: 'Cancelling…',
    rideKeepTrip: 'Keep the ride',
    rideCancelSubmitted: 'Cancellation submitted — the status will update shortly',
    rideCallSupport: 'Call support {{tel}}',
    rideSupplierOrderNo: 'Support ref. {{no}}',
    rideLiveStalled: "Can't reach live updates right now",
  },
  login: {
    title: 'Sign in to Cione',
    subtitle: "You'll come back to what you wrote — nothing is lost.",
    pendingHint: 'After signing in: {{label}}',
    draftKeptHint: "After signing in you'll be back at what you wrote — tap send again.",
    phone: 'Phone',
    phonePlaceholder: 'Your phone number',
    smsCode: 'Code',
    smsCodePlaceholder: '6 digits',
    password: 'Password',
    passwordPlaceholder: 'At least 8 characters',
    sendCode: 'Send code',
    resendIn: 'Resend in {{seconds}}s',
    bySms: 'Sign in with SMS code',
    byPassword: 'Sign in with password',
    submit: 'Sign in',
    close: 'Not now',
    codeSent: 'Code sent',
  },
  me: {
    title: 'Profile',
    nickname: 'Nickname',
    nicknamePlaceholder: 'Not set',
    phone: 'Phone',
    userId: 'User id',
    level: 'Level',
    setPassword: 'Set / change password',
    setPasswordHint: 'Requires an SMS code',
    confirmPhone: 'Phone',
    confirmPhonePlaceholder: 'Type your full number ({{masked}})',
    newPassword: 'New password',
    logout: 'Sign out',
    saved: 'Saved',
    passwordSaved: 'Password updated',
    anonymousBlocked: 'Sign in to manage your profile',
    travelers: 'Travelers',
    orders: 'Orders',
  },
  travelers: {
    title: 'Travelers',
    subtitle: 'Frequent passengers, selectable at booking',
    empty: 'No travelers yet — add one',
    add: 'Add traveler',
    edit: 'Edit',
    remove: 'Remove from roster',
    removeConfirm:
      'Remove “{{name}}” from the roster? Existing orders can still use it — this is not a permanent delete.',
    searchPlaceholder: 'Search by name',
    shared: 'Shared',
    name: 'Name',
    docType: 'Document type',
    docNumber: 'Document number',
    docNumberMasked: 'Document number',
    changeDoc: 'Change document',
    changeDocHint: 'Changing the document type requires the new document number too',
    phone: 'Phone',
    keepUnchangedHint: 'Fields left blank keep their current value',
    keepPhonePlaceholder: 'Currently {{masked}}, blank keeps it',
    surnamePinyin: 'Surname (pinyin)',
    givenNamePinyin: 'Given name (pinyin)',
    birthday: 'Date of birth',
    gender: 'Gender',
    nationality: 'Nationality',
    nationalityCode: 'Nationality code (2-letter)',
    countryOther: 'Other…',
    country: {
      CN: 'China',
      HK: 'Hong Kong SAR, China',
      MO: 'Macao SAR, China',
      TW: 'Taiwan, China',
      US: 'United States',
      GB: 'United Kingdom',
      JP: 'Japan',
      KR: 'South Korea',
      SG: 'Singapore',
      AU: 'Australia',
      CA: 'Canada',
    },
    docValidTo: 'Document valid until',
    save: 'Save',
    saved: 'Saved',
    created: 'Added',
    removed: 'Removed from roster',
    plaintextHint: 'The document number is used only for ticketing — never shown or cached',
    genderMale: 'Male',
    genderFemale: 'Female',
    required: 'Name, document type and number are required',
  },
  history: {
    title: 'History',
    empty: 'No past conversations',
    untitled: '(untitled)',
    messageCount: '{{count}} messages',
    open: 'Open',
    remove: 'Delete',
    removeConfirm: 'Delete',
    removeDone: 'Deleted',
    removedCurrent: 'Deleted. The current chat was cleared.',
    replayTitle: 'Replay',
    continueHere: 'Continue this chat',
    earlier: 'Load earlier',
    noEarlier: 'No earlier messages',
    notFound: 'Conversation not found',
    back: 'Back to list',
    readOnly: 'Read-only replay — go back to the chat to send messages',
  },
  orders: {
    title: 'Orders',
    empty: 'No orders yet',
    all: 'All',
    detail: 'Order detail',
    orderNo: 'Order no.',
    createdAt: 'Created',
    amount: 'Amount',
    supplierOrderNo: 'Supplier order no.',
    notIssuedYet: 'After ticketing',
    cancel: 'Cancel order',
    cancelHint: 'Pre-ticketing cancellation only; contact support for refunds after ticketing',
    cancelConfirm: 'Cancel this order?',
    cancelReason: 'Reason (optional)',
    cancelDone: 'Submitted. Current status: {{state}}',
    notFound: 'Order not found',
    payloadTitle: 'Itinerary',
    payloadBroken: 'Itinerary is temporarily unavailable',
    back: 'Back to list',
    domain: {
      FLIGHT: 'Flight',
      HOTEL: 'Hotel',
      RIDE: 'Ride',
    },
    /* 🔴 Ride amounts are ESTIMATES — the supplier bills the rider directly. */
    estimatedAmount: 'Est. {{price}}',
    /* ⚠⚠ Never say "refunded": cancelling a paid order lands in REFUNDING, and the
       actual refund is sent by a follow-up job, not synchronously. */
    refundStarted: 'Cancelled — your refund is being processed and will go back the way you paid.',
    cancelPaidHint:
      'This order is paid; cancelling refunds it in full. ⚠ Already ticketed? Changing or refunding a ticket is a different process — contact support.',
    pay: {
      title: 'Payment',
      action: 'Pay now',
      actionWithPrice: 'Pay {{price}}',
      unavailableBody:
        'Paying in the browser is not available yet: only WeChat in-app payment is live. Please pay from the Cione app — your order is held and will not expire because of this.',
      /* ⚠⚠ CONFLICT here is a race, not a failure. Keep the tone neutral. */
      raceRetry: 'The order status just changed — please try again in a moment.',
      expiresIn: 'This payment expires in {{time}}',
      expired: 'This payment timed out — just tap pay again.',
      waiting: 'Payment started. Waiting for the result — this page updates automatically.',
      cancelled: 'Payment cancelled. Your order is still here.',
      paid: 'Payment received — your order is being ticketed.',
      paramsBroken:
        'The payment details were incomplete, so we stopped. Please retry or contact support.',
      noPush: 'The order status is the source of truth for payment.',
    },
    state: {
      ORDER_CREATED: 'Created',
      ORDER_PENDING_PAYMENT: 'Pending payment',
      ORDER_PAID: 'Paid',
      ORDER_FULFILLING: 'Ticketing',
      ORDER_COMPLETED: 'Completed',
      ORDER_CANCELLED: 'Cancelled',
      ORDER_REFUNDING: 'Refunding',
      ORDER_REFUNDED: 'Refunded',
      ORDER_FAILED: 'Failed',
    },
  },
  docTypes: {
    IC: 'ID card',
    PP: 'Passport',
    GA: 'HK/Macau permit',
    TB: 'Taiwan permit',
    OTHER: 'Other',
  },
  error: {
    unknown: 'Something went wrong. Please try again.',
    AUTH: {
      LOGIN_REQUIRED: 'Please sign in first',
      TOKEN_EXPIRED: 'Your session expired. Please sign in again.',
      TOKEN_INVALID: 'Your session is no longer valid. Please sign in again.',
      CODE_INVALID: 'That code is not correct',
      CODE_EXPIRED: 'That code expired. Please request a new one.',
      CODE_TOO_FREQUENT: 'Too many requests. Please wait a moment.',
      PASSWORD_INVALID: 'Phone or password is incorrect',
      ACCOUNT_DISABLED: 'This account is disabled. Please contact support.',
      ACTION_FORBIDDEN: 'This step requires signing in',
    },
    COMMON: {
      INTERNAL: 'Server error. Please try again later.',
      INVALID_ARGUMENT: 'Some input is invalid. Please check and retry.',
      FAILED_PRECONDITION: 'This action is not possible in the current state',
      UNAUTHENTICATED: 'Please sign in first',
      FORBIDDEN: 'You do not have permission for this action',
      NOT_FOUND: 'Not found',
      CONFLICT: 'State has changed. Please refresh and retry.',
      RATE_LIMITED: 'Busy right now. Please try again shortly.',
      UPSTREAM_UNAVAILABLE: 'An upstream service is unavailable. Please retry later.',
    },
    IDENTITY: {
      USER_NOT_FOUND: 'User not found',
      EMAIL_EXISTS: 'That email is already in use',
      VERSION_CONFLICT: 'Modified elsewhere. Please refresh and retry.',
      DOC_INVALID: 'Document details are invalid',
      TRAVELER_NOT_FOUND: 'Traveler not found',
      TRAVELER_FORBIDDEN: 'No permission for this traveler',
      TRAVELER_ARCHIVED: 'This traveler is archived',
      VAULT_NOT_CONFIGURED: 'Server configuration missing. Contact support.',
      AUDIT_WRITE_FAILED: 'The action could not be audited and was aborted',
      /* ⚠ Format only — says nothing about whether the token itself works. */
      CREDENTIAL_FORMAT_INVALID: "That doesn't look right — copy the whole token again",
    },
    TRADE: {
      INVENTORY_GONE: 'This flight is no longer bookable — try another one',
      PRICE_CHANGED: 'The price has changed. Please confirm this flight again',
      QUOTE_EXPIRED: 'The quote has expired. Please search again',
      TRAVELER_DOC_INVALID: 'There is a problem with the document — check it in Travelers',
      RESULT_UNKNOWN: 'Result pending — do NOT resubmit. Check your orders for the latest status.',
      UNAVAILABLE: 'Ticketing is temporarily unavailable. Please contact support',
    },
    CLIENT: {
      NETWORK: 'Network unavailable. Check your connection.',
      TIMEOUT: 'Request timed out. Please try again.',
      ABORTED: 'Cancelled',
      CONTRACT_DRIFT: 'Client and server versions differ. Please update.',
    },
  },
} as const

void i18n.use(initReactI18next).init({
  resources: { 'zh-CN': { translation: zhCN }, en: { translation: en } },
  lng: 'zh-CN',
  fallbackLng: 'zh-CN',
  interpolation: { escapeValue: false },
})

export default i18n
export { zhCN, en }
